// client/src/components/billingExecution/BillingReceiptReview.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingReceiptService from '../../services/billingReceiptService';

const BillingReceiptReview = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Current language
  const lang = i18n.language;
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Approval/rejection state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Check if user can approve/reject
  const canApprove = user.role === 'admin' || 
                     (user.role === 'faculty' && 
                      (user.user_type === 'checker' || user.user_type === 'both'));
  
  // Load receipt data
  useEffect(() => {
    const loadReceipt = async () => {
      try {
        setLoading(true);
        const response = await billingReceiptService.getBillingReceipt(id);
        setReceipt(response.data);
        setError('');
      } catch (err) {
        setError(t('errorLoadingReceiptDetails'));
      } finally {
        setLoading(false);
      }
    };
    
    loadReceipt();
  }, [id, t]);
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notSpecified');
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (!value) return formatCurrency(0);
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(parseFloat(value));
  };
  
  // Calculate totals
  const calculateTotals = () => {
    if (!receipt || !receipt.items || !receipt.transaction) {
      return { baseTotal: 0, additionalTotal: 0, grandTotal: 0 };
    }
    
    const baseTotal = receipt.items.length * parseFloat(receipt.transaction.total_value || 0);
    const additionalTotal = receipt.items.reduce((sum, item) => sum + parseFloat(item.additional_value || 0), 0);
    
    return {
      baseTotal,
      additionalTotal,
      grandTotal: baseTotal + additionalTotal
    };
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Get status chip color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Handle approve dialog open
  const handleApproveClick = () => {
    setApproveDialogOpen(true);
  };
  
  // Handle reject dialog open
  const handleRejectClick = () => {
    setRejectDialogOpen(true);
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setApproveDialogOpen(false);
    setRejectDialogOpen(false);
    setComments('');
  };
  
  // Handle comments change
  const handleCommentsChange = (e) => {
    setComments(e.target.value);
  };
  
  // Handle approve confirm
  const handleApproveConfirm = async () => {
    try {
      setActionLoading(true);
      await billingReceiptService.approveReceipt(id, { comments });
      setSuccess(t('receiptApprovedSuccess'));
      
      // Reload receipt data
      const response = await billingReceiptService.getBillingReceipt(id);
      setReceipt(response.data);
      
      handleDialogClose();
    } catch (err) {
      setError(t('errorApprovingReceipt'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle reject confirm
  const handleRejectConfirm = async () => {
    if (!comments) {
      setError(t('commentsRequiredForRejection'));
      return;
    }
    
    try {
      setActionLoading(true);
      await billingReceiptService.rejectReceipt(id, { comments });
      setSuccess(t('receiptRejectedSuccess'));
      
      // Reload receipt data
      const response = await billingReceiptService.getBillingReceipt(id);
      setReceipt(response.data);
      
      handleDialogClose();
    } catch (err) {
      setError(t('errorRejectingReceipt'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const totals = calculateTotals();
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/billing-receipts')}
        >
          {t('back')}
        </Button>
      </Container>
    );
  }
  
  if (!receipt) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">{t('receiptNotFound')}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/billing-receipts')}
          >
            {t('back')}
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('reviewBillingReceipt')}
      </Typography>
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            {t('receiptDetails')} #{receipt.id}
          </Typography>
          
          <Chip 
            label={receipt.is_approved ? t('approved') : t('notApproved')}
            color={receipt.is_approved ? 'success' : 'warning'}
            size="medium"
          />
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('faculty')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {receipt.userFaculty ? receipt.userFaculty[`name_${lang}`] : t('none')}
            </Typography>
            
            <Typography variant="subtitle1">{t('paymentBylaw')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {receipt.paymentBylaw ? receipt.paymentBylaw.name : '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('transactionName')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {receipt.transaction?.paymentItem?.name || '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('baseValuePerStudent')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatCurrency(receipt.transaction?.total_value)}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">{t('creationDate')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatDate(receipt.created_at)}
            </Typography>
            
            <Typography variant="subtitle1">{t('createdBy')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {receipt.creator?.username || '-'}
            </Typography>
            
            <Typography variant="subtitle1">{t('billingDueDate')}:</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {formatDate(receipt.billing_due_date)}
            </Typography>
            
            <Typography variant="subtitle1">{t('status')}:</Typography>
            <Chip 
              label={t(receipt.status)}
              color={getStatusColor(receipt.status)}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>
        </Grid>
        
        {receipt.is_approved && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('approvalDetails')}:
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('approvedBy')}: {receipt.approver?.username || '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {t('approvedAt')}: {formatDate(receipt.approved_at)}
            </Typography>
            {receipt.comments && (
              <Typography variant="body2" sx={{ color: 'white', mt: 1 }}>
                {t('comments')}: {receipt.comments}
              </Typography>
            )}
          </Box>
        )}
        
        {!receipt.is_approved && receipt.comments && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
              {t('reviewComments')}:
            </Typography>
            <Typography variant="body2" sx={{ color: 'white' }}>
              {receipt.comments}
            </Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 3 }} />
        
        <Box sx={{ mb: 2 }}>
          <Grid container>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">{t('totalStudents')}:</Typography>
              <Typography variant="body1" fontWeight="bold">{receipt.items?.length || 0}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">{t('baseTotal')}:</Typography>
              <Typography variant="body1" fontWeight="bold">{formatCurrency(totals.baseTotal)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1">{t('additionalTotal')}:</Typography>
              <Typography variant="body1" fontWeight="bold">{formatCurrency(totals.additionalTotal)}</Typography>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              {t('grandTotal')}: {formatCurrency(totals.grandTotal)}
            </Typography>
          </Box>
        </Box>
        
        {/* Approval/rejection buttons - only show if not already approved and user has permission */}
        {!receipt.is_approved && canApprove && (
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<ApproveIcon />}
              onClick={handleApproveClick}
            >
              {t('approveReceipt')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<RejectIcon />}
              onClick={handleRejectClick}
            >
              {t('rejectReceipt')}
            </Button>
          </Box>
        )}
      </Paper>
      
      <Typography variant="h6" gutterBottom>
        {t('studentDetails')} 
        <InfoIcon 
          color="info" 
          fontSize="small" 
          sx={{ ml: 1, verticalAlign: 'middle' }} 
          titleAccess={t('studentDetailsHelp')}
        />
      </Typography>
      
      {receipt.items && receipt.items.length > 0 ? (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="billing items table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('nationalID')}</TableCell>
                  <TableCell>{t('nameEN')}</TableCell>
                  <TableCell>{t('nameAR')}</TableCell>
                  <TableCell align="right">{t('baseValue')}</TableCell>
                  <TableCell align="right">{t('additionalValue')}</TableCell>
                  <TableCell align="right">{t('totalValue')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {receipt.items
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.national_id}</TableCell>
                      <TableCell>{item.name_en}</TableCell>
                      <TableCell>{item.name_ar}</TableCell>
                      <TableCell align="right">{formatCurrency(receipt.transaction.total_value)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.additional_value)}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(parseFloat(receipt.transaction.total_value || 0) + parseFloat(item.additional_value || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={receipt.items.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('rowsPerPage')}
          />
        </Paper>
      ) : (
        <Alert severity="info">
          {t('noItemsFound')}
        </Alert>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/billing-receipts')}
        >
          {t('backToReceipts')}
        </Button>
      </Box>
      
      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('approveReceipt')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('approveReceiptConfirmation')}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="comments"
            name="comments"
            label={t('comments')}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={comments}
            onChange={handleCommentsChange}
            placeholder={t('approveCommentsPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t('cancel')}</Button>
          <Button 
            onClick={handleApproveConfirm} 
            color="success" 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? t('approving') : t('approve')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('rejectReceipt')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('rejectReceiptConfirmation')}
          </DialogContentText>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('commentsRequiredWarning')}
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            id="comments"
            name="comments"
            label={t('comments')}
            type="text"
            fullWidth
            multiline
            rows={4}
            required
            value={comments}
            onChange={handleCommentsChange}
            placeholder={t('rejectCommentsPlaceholder')}
            error={rejectDialogOpen && !comments}
            helperText={rejectDialogOpen && !comments ? t('commentsRequired') : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t('cancel')}</Button>
          <Button 
            onClick={handleRejectConfirm} 
            color="error" 
            variant="contained"
            disabled={actionLoading || !comments}
          >
            {actionLoading ? t('rejecting') : t('reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BillingReceiptReview;